/* eslint-disable @typescript-eslint/no-explicit-any */
import { getConfig } from "@edx/frontend-platform";
import { getAuthenticatedHttpClient } from "@edx/frontend-platform/auth";
import {
  getLearningDemoApiResponse,
  signDemoActivityRequest
} from "@/demo/learningDemoApi";
import { isLearningDemoMode } from "@/demo/isLearningDemoMode";
import { getBaseRoute } from "@utils/route.ts";

export const getApiBaseUrl = (): string => {
  const { LMS_BASE_URL } = getConfig();
  return `${LMS_BASE_URL}/euka-api/v1`;
};

const getLoginUrl = (): string => {
  const { LMS_BASE_URL } = getConfig();
  return `${LMS_BASE_URL}/login`;
};

const get404Url = (): string => {
  return `${getBaseRoute()}not-found`;
};

const getMaintenanceUrl = (): string => {
  return `${getBaseRoute()}maintenance`;
};

const joinUrl = (base: string, path: string) =>
  `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;

const extractHttpStatus = (error: any): number | undefined =>
  error?.customAttributes?.httpErrorStatus ??
  error?.response?.status ??
  error?.status;

/**
 * Detect errors originating from the @edx/frontend-platform JWT token refresh
 * interceptor (as opposed to an actual API response).
 *
 * When a JWT cookie expires (every ~30 s), the Axios request interceptor
 * attempts to refresh the token by POSTing to `/login_refresh` BEFORE the
 * real API request fires.  If that refresh POST fails with a 5xx (e.g. the
 * user opens many lesson tabs at once and the burst of concurrent refresh
 * requests overwhelms the LMS), the error propagates through
 * processAxiosError with `httpErrorStatus` 500/502/503 — but the
 * `httpErrorRequestUrl` points at the token-refresh endpoint, NOT at the
 * euka-api endpoint the caller wanted.
 *
 * Without this check, handleHttpErrors would treat the error as a genuine
 * API 5xx and redirect to the "Technical Turbulence" page even though the
 * real API was never called.
 */
const isTokenRefreshError = (error: any): boolean => {
  const url: string =
    error?.customAttributes?.httpErrorRequestUrl ??
    error?.config?.url ??
    "";
  const status = extractHttpStatus(error) || 0;

  // Only treat 5xx as a transient refresh error worth retrying.
  // A 401 from the refresh endpoint means the session is genuinely dead
  // (refresh token expired / revoked) — that should fall through to the
  // normal handleHttpErrors → redirect-to-login flow, not be retried.
  return (status >= 500 && (url.includes("/login_refresh") || url.includes("/token/refresh")));
};

/**
 * Maximum number of times to retry an API call when the failure is
 * potentially transient (JWT token-refresh 5xx, or 502 Bad Gateway).
 *
 * Uses exponential backoff: 1s, 2s — so the server has time to
 * recover without being hammered by immediate retries.
 */
const TRANSIENT_MAX_RETRIES = 2;
const TRANSIENT_BASE_DELAY_MS = 1000;

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Check whether an error is transient and worth retrying.
 *
 * Retryable cases:
 *   - JWT token refresh 5xx — the real API call never fired; the pre-flight
 *     token refresh failed.  Retrying gives it another chance.
 *   - HTTP 502/503/504 — upstream is briefly unhealthy (proxy hiccup,
 *     pod restart, ELB scale-up, gateway timeout).  Typically resolves
 *     in seconds.  For 503 specifically: if retries also fail,
 *     handleHttpErrors falls through to the /maintenance redirect, so a
 *     genuine maintenance window still gets the right UX — only the
 *     transient case is absorbed.
 *   - Axios "config" errors — the error was thrown inside the @edx/frontend-
 *     platform JWT request interceptor BEFORE the HTTP request fired, so
 *     both error.request and error.response are absent. This happens during
 *     the ~5-10 s ALB deregistration lag after a pod receives SIGTERM: new
 *     requests still reach the dying pod, whose auth interceptor can't
 *     complete the JWT refresh and throws instead of returning an HTTP error.
 *     Up to 2 retries (1 s then 2 s back-off) cover the drain window.
 *     Cancelled requests (ERR_CANCELED / CanceledError) are explicitly
 *     excluded — those happen on component unmount and must not be retried.
 */
const RETRYABLE_STATUSES = new Set([502, 503, 504]);

/**
 * Returns true for Axios errors that were thrown inside an interceptor
 * before any HTTP request was made (httpErrorType: 'config').
 *
 * Explicitly excludes request-cancellation errors (e.g. React Query
 * AbortController on component unmount) which share the same shape but
 * must not be retried.
 */
const isConfigError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const err = error as any;
  // Cancelled requests share the config-error shape but should not be retried
  if (err.code === "ERR_CANCELED" || err.name === "CanceledError") return false;
  return err.isAxiosError === true && !err.request && !err.response;
};

const isRetryableError = (error: unknown): boolean => {
  if (isTokenRefreshError(error)) return true;
  if (isConfigError(error)) return true;
  const status = extractHttpStatus(error) ?? 0;
  return RETRYABLE_STATUSES.has(status);
};

let isRedirectingToLogin = false;

// ── Server error state (observable from React) ───────────────
//
// Instead of hard-redirecting to /learning/server-error (which changes
// the URL and traps the student on the error page), we set a flag that
// React reads via useSyncExternalStore.  The current route stays in the
// address bar so the student can simply refresh to retry.
//
// The flag is cleared explicitly by clearServerError() — either from the
// "Return to dashboard" button or from a page refresh (which re-inits
// the module with hasServerError = false).

let serverErrorSnapshot: { hasError: boolean; status?: number } = {
  hasError: false
};
const serverErrorListeners = new Set<() => void>();

const notifyServerErrorListeners = () => {
  serverErrorListeners.forEach(listener => listener());
};

const setServerErrorState = (hasError: boolean, status?: number) => {
  // Only create a new snapshot object when the value actually changes.
  // useSyncExternalStore uses Object.is to compare snapshots — returning
  // a new object every time would cause infinite re-renders.
  if (serverErrorSnapshot.hasError !== hasError || serverErrorSnapshot.status !== status) {
    serverErrorSnapshot = { hasError, status };
    notifyServerErrorListeners();
  }
};

/**
 * Subscribe to server error state changes. Returns an unsubscribe function.
 * Used by useServerError() hook via useSyncExternalStore.
 */
export const subscribeServerError = (listener: () => void): (() => void) => {
  serverErrorListeners.add(listener);
  return () => serverErrorListeners.delete(listener);
};

/** Snapshot for useSyncExternalStore — returns a stable reference. */
export const getServerErrorSnapshot = () => serverErrorSnapshot;

/** Clear the server error flag. Called by the error page UI. */
export const clearServerError = (): void => {
  setServerErrorState(false);
};

const handleHttpErrors = (error: any) => {
  const status = extractHttpStatus(error);

  // On 401 (session expired), redirect to login instead of logout.
  // This allows re-authentication without clearing Portal sessions --
  // only an explicit user-initiated logout should invalidate Portal tokens.
  // Pass the current URL as ?next= so the student returns to their exact
  // page (lesson, assessment) after re-authentication, not the dashboard.
  if (status === 401 && !isRedirectingToLogin) {
    isRedirectingToLogin = true;
    window.location.href = `${getLoginUrl()}?next=${encodeURIComponent(window.location.href)}`;
    return;
  }
  if (status === 404) {
    window.location.href = get404Url();
    return;
  }

  // Handle 5xx server errors — render error page in-place (no redirect).
  // 503 hard-redirects to the maintenance page since that's a
  // deliberately different UX (scheduled downtime notice).  503s that
  // are actually transient (pod restart, ELB scale-up) are absorbed by
  // withTransientRetry before reaching here; we only redirect when the
  // retries also fail, which is the genuine-maintenance case.
  if (status !== undefined && status >= 500) {
    if (status === 503) {
      window.location.href = getMaintenanceUrl();
    } else {
      setServerErrorState(true, status);
    }
  }
};

// Cache the client promise to avoid concurrent init races
let clientPromise: ReturnType<typeof getAuthenticatedHttpClient> | null = null;
const getClient = () => (clientPromise ??= getAuthenticatedHttpClient());

/**
 * Simulates API network latency for specific endpoints in demo mode.
 * Used to make loading states (e.g. the FunFactPanel dot loader) visible
 * during reviews without needing a slow real network. See BLD2-376.
 */
const demoPause = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export interface GetOptions {
  /**
   * When `true`, skip the global error handler (which redirects on 404, 401,
   * 5xx etc.).  The caller is responsible for handling all HTTP errors itself.
   *
   * Use this for non-critical requests where a failure should NOT redirect
   * the whole page.
   */
  suppressGlobalErrors?: boolean;
}

/**
 * Execute an API call with automatic retry for transient errors.
 *
 * Retryable errors are defined by isRetryableError().  Non-retryable errors
 * (500, 4xx, etc.) are passed straight through to handleHttpErrors on the
 * first occurrence.
 *
 * If all retries are exhausted the error is handed to handleHttpErrors
 * normally — which renders the error page in-place for most 5xx, or
 * redirects for 401/404/503.
 */
const withTransientRetry = async <T>(
  apiCall: () => Promise<T>,
  endpoint: string,
  suppressGlobalErrors: boolean
): Promise<T> => {
  let lastError: unknown;

  for (let attempt = 0; attempt <= TRANSIENT_MAX_RETRIES; attempt++) {
    try {
      return await apiCall();
    } catch (error) {
      lastError = error;

      if (!isRetryableError(error)) {
        // Not a transient error — handle immediately, no retry.
        if (!suppressGlobalErrors) {
          handleHttpErrors(error);
        }
        throw error;
      }

      // Transient error — retry if we have attempts left
      if (attempt < TRANSIENT_MAX_RETRIES) {
        const delay = TRANSIENT_BASE_DELAY_MS * Math.pow(2, attempt); // 1s, 2s
        const status = extractHttpStatus(error) ?? (isConfigError(error) ? "config" : "unknown");
        console.warn(
          `[httpClient] transient error ${status} for ${endpoint} ` +
          `(attempt ${attempt + 1}/${TRANSIENT_MAX_RETRIES}), ` +
          `retrying in ${delay}ms…`
        );
        await wait(delay);
      }
    }
  }

  // All retries exhausted — log and let the normal error handling take over.
  const finalStatus = extractHttpStatus(lastError) ?? "unknown";
  console.error(
    `[httpClient] transient error ${finalStatus} for ${endpoint} ` +
    `after ${TRANSIENT_MAX_RETRIES} retries — giving up.`
  );
  if (!suppressGlobalErrors) {
    handleHttpErrors(lastError);
  }
  throw lastError;
};

export const get = async <T>(
  endpoint: string,
  options?: GetOptions
): Promise<T> => {
  if (isLearningDemoMode()) {
    const demoResponse = getLearningDemoApiResponse("GET", endpoint);

    if (demoResponse !== undefined) {
      // Simulate realistic API latency for the fun-fact endpoint so the
      // loading state (dot loader inside the speech bubble) is visible
      // during demo reviews. See BLD2-376 and learningDemoApi.ts.
      if (endpoint.startsWith("/fun-fact/today")) {
        await demoPause(2000);
      }
      // Section content responses need async Learnosity signing
      const signed = await signDemoActivityRequest(demoResponse);
      return signed as T;
    }

    console.warn(`[learning-demo] No fixture configured for GET ${endpoint}`);
    return {} as T;
  }

  return withTransientRetry(
    async () => {
      const client = await getClient();
      const response = await client.get(joinUrl(getApiBaseUrl(), endpoint));
      return response.data as T;
    },
    endpoint,
    options?.suppressGlobalErrors ?? false
  );
};

export interface PostOptions {
  /**
   * When `true`, skip the global error handler (which redirects on 401, 5xx
   * etc.).  The caller is responsible for handling all HTTP errors itself.
   *
   * Use this when the caller needs to retry or handle specific status codes
   * without the global handler redirecting the page first.
   */
  suppressGlobalErrors?: boolean;
}

export const post = <T>(
  endpoint: string,
  payload?: any,
  options?: PostOptions
): Promise<T> => {
  if (isLearningDemoMode()) {
    const demoResponse = getLearningDemoApiResponse("POST", endpoint, payload);

    if (demoResponse !== undefined) {
      return Promise.resolve(demoResponse as T);
    }

    console.warn(`[learning-demo] No fixture configured for POST ${endpoint}`);
    return Promise.resolve({
      success: true,
      message: "ok"
    } as T);
  }

  return withTransientRetry(
    async () => {
      const client = await getClient();
      const response = await client.post(
        joinUrl(getApiBaseUrl(), endpoint),
        payload
      );
      return response.data as T;
    },
    endpoint,
    options?.suppressGlobalErrors ?? false
  );
};

export const del = <T>(endpoint: string, payload?: any, options?: PostOptions): Promise<T> => {
  if (isLearningDemoMode()) {
    const demoResponse = getLearningDemoApiResponse("POST", endpoint, payload);

    if (demoResponse !== undefined) {
      return Promise.resolve(demoResponse as T);
    }

    return Promise.resolve({ success: true, message: "ok" } as T);
  }

  return withTransientRetry(
    async () => {
      const client = await getClient();
      const response = await client.delete(joinUrl(getApiBaseUrl(), endpoint), {
        data: payload
      });
      return response.data as T;
    },
    endpoint,
    options?.suppressGlobalErrors ?? false
  );
};
